/**
 * SuperAdmin → detalle de una cuenta: sus empresas (actividad, certificado, ambiente de
 * Hacienda, rechazos), usuarios, respaldos, datos del plan y acciones.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  ArrowLeft, RefreshCw, Building2, Users, DatabaseBackup, Settings2, LogIn, KeyRound, PauseCircle, PlayCircle,
  Trash2, ShieldAlert, Save, Copy, Check, AlertTriangle, Palette, Layers
} from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import ConfirmDangerModal from '../components/ConfirmDangerModal';
import { panelApi, tenantsApi } from '../utils/api';
import SelectorDeTema, { TEMAS } from '../../components/SelectorDeTema';
import {
  Panel, Boton, Cargando, Aviso, Vacio, Kpi, Modal, Campo, inputCls, EstadoCuenta, Severidad,
  fNum, fColones, fMonto, fBytes, fFecha, fFechaHora, hace
} from '../components/ui';

const PESTANAS = [
  ['empresas', 'Empresas', Building2], ['usuarios', 'Usuarios', Users], ['respaldos', 'Respaldos', DatabaseBackup], ['datos', 'Datos y plan', Settings2]
];

function Certificado({ vence }) {
  if (!vence) return <span className="text-amber-700 text-xs font-medium">Sin certificado</span>;
  const dias = Math.floor((new Date(vence).getTime() - Date.now()) / 86400000);
  const cls = dias < 0 ? 'text-red-700' : dias <= 30 ? 'text-amber-700' : 'text-gray-700';
  return <span className={clsx('text-sm', cls)} title={`Vence el ${fFecha(vence)}`}>{fFecha(vence)}<span className="block text-xs text-gray-500">{dias < 0 ? `vencido hace ${-dias} d` : `${dias} días`}</span></span>;
}

function Ambiente({ valor }) {
  if (!valor) return <span className="text-xs text-gray-400">—</span>;
  const prod = /prod/i.test(valor);
  return <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium border', prod ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200')}>{prod ? 'Producción' : 'Pruebas'}</span>;
}

export default function CuentaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [aviso, setAviso] = useState(null);
  // Apariencia de la cuenta (tema de la consola), y aplicar un tema a todas las cuentas
  const [tema, setTema] = useState(null);
  const [temaGuardando, setTemaGuardando] = useState(null);
  const [temaTodas, setTemaTodas] = useState(null);
  const [tab, setTab] = useState('empresas');
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [suspender, setSuspender] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [eliminar, setEliminar] = useState(false);
  const [clave, setClave] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async (forzar = false) => {
    forzar ? setRefrescando(true) : setCargando(true);
    try {
      const r = await panelApi.cuenta(id, forzar);
      const data = r.data.data;
      setD(data);
      const t = data.tenant;
      setForm({ name: t.name, plan: t.plan, contact_email: t.contact_email || '', contact_phone: t.contact_phone || '', billing_email: t.billing_email || '',
        max_users: t.max_users || 5, max_emisores: t.max_emisores || 3, trial_ends_at: t.trial_ends_at ? String(t.trial_ends_at).slice(0, 10) : '', notes: t.notes || '' });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo cargar la cuenta' });
    } finally {
      setCargando(false);
      setRefrescando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);
  const [planes, setPlanes] = useState([]);
  useEffect(() => { panelApi.planes().then(r => setPlanes(r.data.planes || [])).catch(() => {}); }, []);

  const error = (e, def) => setAviso({ tipo: 'error', texto: e.response?.data?.error || def });

  useEffect(() => {
    panelApi.aparienciaCuenta(id).then(r => setTema(r.data.tema)).catch(() => setTema(null));
  }, [id]);

  const elegirTema = async (nuevo) => {
    setTemaGuardando(nuevo);
    try {
      const r = await panelApi.cambiarAparienciaCuenta(id, nuevo);
      setTema(r.data.tema);
      setAviso({ tipo: 'ok', texto: `Tema de la cuenta: ${TEMAS.find(x => x.id === r.data.tema)?.nombre}` });
    } catch (e) { error(e, 'No se pudo cambiar el tema'); }
    finally { setTemaGuardando(null); }
  };

  const aplicarATodas = async () => {
    setOcupado('todas');
    try {
      const r = await panelApi.aparienciaTodas(temaTodas);
      const nombre = TEMAS.find(x => x.id === temaTodas)?.nombre;
      setAviso(r.data.fallidas?.length
        ? { tipo: 'error', texto: `${nombre}: aplicado en ${r.data.aplicadas.length}, falló en ${r.data.fallidas.map(f => f.slug).join(', ')}` }
        : { tipo: 'ok', texto: `${nombre} aplicado en ${r.data.aplicadas.length} cuenta(s)` });
      if (!r.data.fallidas?.length) setTema(temaTodas);
    } catch (e) { error(e, 'No se pudo aplicar el tema a todas las cuentas'); }
    finally { setOcupado(null); setTemaTodas(null); }
  };
  const t = d?.tenant;

  const entrar = async (ir = '/dashboard') => {
    setOcupado('entrar');
    try {
      const r = await tenantsApi.impersonate(t.id);
      const { token, tenant_slug } = r.data;
      window.open(`/impersonar?token=${encodeURIComponent(token)}&slug=${encodeURIComponent(tenant_slug)}&ir=${encodeURIComponent(ir)}`, '_blank');
    } catch (e) { error(e, 'No se pudo abrir la sesión de soporte'); } finally { setOcupado(null); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await tenantsApi.update(t.id, { ...form, trial_ends_at: form.trial_ends_at || null });
      setAviso({ tipo: 'ok', texto: 'Datos de la cuenta guardados' });
      cargar(true);
    } catch (e) { error(e, 'No se pudo guardar'); } finally { setGuardando(false); }
  };

  const confirmarSuspender = async () => {
    setOcupado('suspender');
    try {
      await tenantsApi.suspend(t.id, motivo.trim());
      setSuspender(false); setMotivo('');
      setAviso({ tipo: 'ok', texto: 'Cuenta suspendida: sus usuarios ya no pueden ingresar' });
      cargar(true);
    } catch (e) { error(e, 'No se pudo suspender'); } finally { setOcupado(null); }
  };

  const activar = async () => {
    setOcupado('activar');
    try {
      await tenantsApi.activate(t.id);
      setAviso({ tipo: 'ok', texto: 'Cuenta activada' });
      cargar(true);
    } catch (e) { error(e, 'No se pudo activar'); } finally { setOcupado(null); }
  };

  const resetear = async () => {
    setOcupado('reset');
    try {
      const r = await tenantsApi.resetPassword(t.id);
      setClave({ clave: r.data.temp_password, email: r.data.admin_email || r.data.email });
    } catch (e) { error(e, 'No se pudo generar la contraseña'); } finally { setOcupado(null); }
  };

  const borrar = async () => {
    await tenantsApi.delete(t.id, t.slug);
    navigate('/superadmin/cuentas');
  };

  return (
    <SuperadminLayout>
      <Link to="/superadmin/cuentas" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3"><ArrowLeft className="h-4 w-4" />Cuentas</Link>
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      {cargando ? <Cargando texto="Consultando la base de la cuenta…" /> : d && (
        <div className="space-y-6">
          {/* Encabezado */}
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-gray-900">{t.name}</h1>
                <EstadoCuenta estado={t.status} />
              </div>
              <p className="text-sm text-gray-500 mt-1">{t.slug} · plan {t.plan} · creada {fFecha(t.created_at)}{t.contact_email ? ` · ${t.contact_email}` : ''}</p>
              {t.status === 'suspended' && t.suspension_reason && <p className="text-sm text-amber-700 mt-1">Suspendida: {t.suspension_reason}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Boton icono={RefreshCw} cargando={refrescando} onClick={() => cargar(true)}>Actualizar</Boton>
              <Boton variante="azul" icono={LogIn} cargando={ocupado === 'entrar'} disabled={t.status !== 'active'} onClick={() => entrar()}
                title="Abre la cuenta como su administrador (30 min) en otra pestaña">Entrar como la cuenta</Boton>
              <Boton icono={KeyRound} cargando={ocupado === 'reset'} onClick={resetear}>Contraseña temporal</Boton>
              {t.status === 'active'
                ? <Boton variante="ambar" icono={PauseCircle} onClick={() => setSuspender(true)}>Suspender</Boton>
                : <Boton variante="verde" icono={PlayCircle} cargando={ocupado === 'activar'} onClick={activar}>Activar</Boton>}
            </div>
          </div>

          {d.error && <Aviso aviso={{ tipo: 'error', texto: `No se pudo leer la base de la cuenta: ${d.error}` }} />}

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <Kpi icono={Building2} color="blue" titulo="Empresas activas" valor={d.empresas.filter(e => e.activa).length}
              detalle={t.max_emisores ? `de ${t.max_emisores} del plan` : null} />
            <Kpi icono={Users} color="purple" titulo="Usuarios activos" valor={d.usuarios.activos}
              detalle={`${d.usuarios.activos30d} ingresaron en 30 días${t.max_users ? ` · plan ${t.max_users}` : ''}`} />
            <Kpi icono={Building2} color="green" titulo="Comprobantes este mes" valor={fNum(d.comprobantesMes)} detalle={`último ${hace(d.ultimoComprobante)}`} />
            <Kpi icono={Building2} color="amber" titulo="Facturado este mes" valor={fColones(d.facturadoMesCRC, true)} detalle="neto de NC" />
            <Kpi icono={DatabaseBackup} color="slate" titulo="Base de datos" valor={fBytes(d.baseBytes)} detalle={`${d.rechazados30d} rechazo(s) en 30 días`} />
          </div>

          {/* Alertas */}
          {d.alertas.length > 0 && (
            <Panel titulo="Requiere atención" icono={ShieldAlert} sinPadding>
              <ul className="divide-y divide-gray-100">
                {d.alertas.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 px-5 py-3">
                    <Severidad nivel={a.severidad} />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{a.texto}</p>
                      {a.empresa && <p className="text-xs text-gray-500">{a.empresa}</p>}
                    </div>
                    {a.tipo === 'hacienda' && <Link to={`/superadmin/rechazos?cuenta=${t.id}`} className="text-xs text-blue-600 hover:underline shrink-0">Ver casos</Link>}
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* Pestañas */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
            {PESTANAS.map(([k, label, I]) => (
              <button key={k} onClick={() => setTab(k)}
                className={clsx('flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium', tab === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}>
                <I className="h-4 w-4" />{label}
              </button>
            ))}
          </div>

          {tab === 'empresas' && (
            <Panel sinPadding>
              {d.empresas.length === 0 ? <Vacio icono={Building2} titulo="Sin empresas" /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left font-medium px-5 py-2.5">Empresa</th>
                        <th className="text-left font-medium px-3 py-2.5">Hacienda</th>
                        <th className="text-left font-medium px-3 py-2.5">Certificado vence</th>
                        <th className="text-right font-medium px-3 py-2.5">Comprobantes mes</th>
                        <th className="text-right font-medium px-3 py-2.5">Facturado mes</th>
                        <th className="text-right font-medium px-3 py-2.5">Rechazos 30 d</th>
                        <th className="text-right font-medium px-3 py-2.5">Sin respuesta</th>
                        <th className="text-left font-medium px-3 py-2.5">Último comprobante</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {d.empresas.map(e => (
                        <tr key={e.id} className={clsx(!e.activa && 'opacity-50')}>
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-900">{e.nombre}{!e.activa && <span className="ml-2 text-xs text-gray-500">(inactiva)</span>}</p>
                            <p className="text-xs text-gray-500">{e.identificacion}{e.nombreComercial && e.nombreComercial !== e.nombre ? ` · ${e.nombreComercial}` : ''}</p>
                          </td>
                          <td className="px-3 py-3"><Ambiente valor={e.ambiente} /></td>
                          <td className="px-3 py-3"><Certificado vence={e.certificadoVence} /></td>
                          <td className="px-3 py-3 text-right font-medium text-gray-900">{fNum(e.comprobantesMes)}</td>
                          <td className="px-3 py-3 text-right text-gray-900">
                            {Object.keys(e.porMoneda).length > 1
                              ? Object.entries(e.porMoneda).map(([m, v]) => <span key={m} className="block">{fMonto(v, m)}</span>)
                              : fColones(e.facturadoMesCRC)}
                          </td>
                          <td className={clsx('px-3 py-3 text-right', e.rechazados30d ? 'text-amber-700 font-medium' : 'text-gray-500')}>{e.rechazados30d}</td>
                          <td className={clsx('px-3 py-3 text-right', e.atascados ? 'text-red-700 font-medium' : 'text-gray-500')}>{e.atascados}</td>
                          <td className="px-3 py-3 text-gray-600" title={fFechaHora(e.ultimoComprobante)}>{hace(e.ultimoComprobante)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}

          {tab === 'usuarios' && (
            <Panel sinPadding>
              {d.usuariosLista.length === 0 ? <Vacio icono={Users} titulo="Sin usuarios" /> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left font-medium px-5 py-2.5">Usuario</th>
                      <th className="text-left font-medium px-3 py-2.5">Rol</th>
                      <th className="text-right font-medium px-3 py-2.5">Empresas</th>
                      <th className="text-left font-medium px-3 py-2.5">Último ingreso</th>
                      <th className="text-left font-medium px-3 py-2.5">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.usuariosLista.map(u => (
                      <tr key={u.id}>
                        <td className="px-5 py-3"><p className="font-medium text-gray-900">{u.nombre || '—'}</p><p className="text-xs text-gray-500">{u.email}</p></td>
                        <td className="px-3 py-3 text-gray-700">{u.rol}{u.esAdmin && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">admin</span>}</td>
                        <td className="px-3 py-3 text-right text-gray-700">{u.empresas}</td>
                        <td className="px-3 py-3 text-gray-600" title={fFechaHora(u.ultimoLogin)}>{hace(u.ultimoLogin)}</td>
                        <td className="px-3 py-3">{u.activo ? <span className="text-xs text-emerald-700">Activo</span> : <span className="text-xs text-gray-500">Inactivo</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          )}

          {tab === 'respaldos' && (
            <Panel sinPadding accion={null}>
              {d.respaldos.length === 0 ? <Vacio icono={DatabaseBackup} titulo="Esta cuenta no tiene respaldos" texto="Se pueden crear desde Respaldos." /> : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left font-medium px-5 py-2.5">Fecha</th>
                      <th className="text-left font-medium px-3 py-2.5">Alcance</th>
                      <th className="text-left font-medium px-3 py-2.5">Tipo</th>
                      <th className="text-right font-medium px-3 py-2.5">Tamaño</th>
                      <th className="text-left font-medium px-3 py-2.5">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {d.respaldos.map(r => (
                      <tr key={r.id}>
                        <td className="px-5 py-2.5 text-gray-900">{fFechaHora(r.created_at)}</td>
                        <td className="px-3 py-2.5 text-gray-700">{r.alcance === 'empresa' ? r.emisor_nombre : 'Cuenta completa'}</td>
                        <td className="px-3 py-2.5 text-gray-600">{r.tipo === 'automatico' ? 'Automático' : r.tipo === 'antes_de_restaurar' ? 'Antes de restaurar' : 'Manual'}</td>
                        <td className="px-3 py-2.5 text-right text-gray-700">{fBytes(r.size_bytes)}</td>
                        <td className="px-3 py-2.5">
                          <span className={clsx('text-xs font-medium', r.estado === 'exitoso' ? 'text-emerald-700' : r.estado === 'fallido' ? 'text-red-700' : 'text-blue-700')} title={r.error_message || ''}>
                            {r.estado === 'exitoso' ? 'Exitoso' : r.estado === 'fallido' ? 'Fallido' : 'En curso'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="px-5 py-3 border-t border-gray-100"><Link to="/superadmin/respaldos" className="text-sm text-blue-600 hover:underline">Ir a Respaldos</Link></div>
            </Panel>
          )}

          {tab === 'datos' && form && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <Panel className="xl:col-span-2" titulo="Datos y plan" icono={Settings2}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Campo label="Nombre de la cuenta"><input className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Campo>
                  <Campo label="Plan">
                    <select className={inputCls} value={form.plan} onChange={e => {
                      const pl = planes.find(x => x.codigo === e.target.value);
                      setForm({ ...form, plan: e.target.value, ...(pl ? { max_users: pl.maxUsuarios, max_emisores: pl.maxEmpresas } : {}) });
                    }}>
                      {!planes.some(x => x.codigo === form.plan) && <option value={form.plan}>{form.plan}</option>}
                      {planes.filter(x => x.activo || x.codigo === form.plan).map(x => (
                        <option key={x.codigo} value={x.codigo}>{x.nombre} · US${x.precioMensual}/mes · {x.maxEmpresas} empresa{x.maxEmpresas > 1 ? 's' : ''}, {x.maxUsuarios} usuarios</option>
                      ))}
                    </select>
                  </Campo>
                  <Campo label="Correo de contacto"><input className={inputCls} value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></Campo>
                  <Campo label="Teléfono"><input className={inputCls} value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></Campo>
                  <Campo label="Correo de facturación"><input className={inputCls} value={form.billing_email} onChange={e => setForm({ ...form, billing_email: e.target.value })} /></Campo>
                  <Campo label="Fin del período de prueba" ayuda="Vacío = sin período de prueba">
                    <input type="date" className={inputCls} value={form.trial_ends_at} onChange={e => setForm({ ...form, trial_ends_at: e.target.value })} />
                  </Campo>
                  <Campo label="Máximo de usuarios" ayuda={`Hoy usa ${d.usuarios.activos}`}><input type="number" min="1" className={inputCls} value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} /></Campo>
                  <Campo label="Máximo de empresas" ayuda={`Hoy usa ${d.empresas.filter(e => e.activa).length}`}><input type="number" min="1" className={inputCls} value={form.max_emisores} onChange={e => setForm({ ...form, max_emisores: e.target.value })} /></Campo>
                  <Campo label="Notas internas" className="md:col-span-2"><textarea rows={3} className={inputCls} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Campo>
                </div>
                <div className="flex justify-end mt-4"><Boton variante="primario" icono={Save} cargando={guardando} onClick={guardar}>Guardar cambios</Boton></div>
              </Panel>
              <Panel className="xl:col-span-3" titulo="Apariencia de la consola" icono={Palette}
                accion={tema && <Boton icono={Layers} onClick={() => setTemaTodas(tema)}>Usar este tema en todas las cuentas</Boton>}>
                <p className="text-sm text-gray-600 mb-4">Aplica a todas las empresas y usuarios de esta cuenta. El administrador de la cuenta también lo puede cambiar en Configuración → Apariencia.</p>
                {tema ? <SelectorDeTema tema={tema} guardando={temaGuardando} onElegir={elegirTema} /> : <p className="text-sm text-gray-500">No se pudo leer el tema de la cuenta.</p>}
              </Panel>
              <Panel titulo="Zona de riesgo" icono={AlertTriangle}>
                <p className="text-sm text-gray-600">Eliminar la cuenta borra su base de datos completa, con todas sus empresas y comprobantes. No se puede deshacer.</p>
                <Boton variante="rojo" icono={Trash2} className="mt-4" onClick={() => setEliminar(true)}>Eliminar cuenta</Boton>
              </Panel>
            </div>
          )}
        </div>
      )}

      {suspender && (
        <Modal titulo={`Suspender ${t.name}`} icono={PauseCircle} onClose={() => setSuspender(false)} ancho="max-w-xl"
          pie={<>
            <Boton onClick={() => setSuspender(false)}>Cancelar</Boton>
            <Boton variante="ambar" icono={PauseCircle} disabled={motivo.trim().length < 5} cargando={ocupado === 'suspender'} onClick={confirmarSuspender}>Suspender</Boton>
          </>}>
          <p className="text-sm text-gray-600 mb-3">Ningún usuario de la cuenta podrá ingresar hasta que se active de nuevo. Los datos no se tocan.</p>
          <Campo label="Motivo (queda en la auditoría)"><input autoFocus className={inputCls} value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej.: falta de pago" /></Campo>
        </Modal>
      )}

      {temaTodas && (
        <Modal titulo="Tema para todas las cuentas" icono={Palette} onClose={() => setTemaTodas(null)} ancho="max-w-lg"
          pie={<>
            <Boton onClick={() => setTemaTodas(null)}>Cancelar</Boton>
            <Boton variante="primario" icono={Layers} cargando={ocupado === 'todas'} onClick={aplicarATodas}>Aplicar a todas</Boton>
          </>}>
          <p className="text-sm text-gray-600">Todas las cuentas activas van a usar <strong>{TEMAS.find(x => x.id === temaTodas)?.nombre}</strong>. Cada administrador lo puede volver a cambiar en su cuenta.</p>
        </Modal>
      )}

      {clave && (
        <Modal titulo="Contraseña temporal" icono={KeyRound} onClose={() => setClave(null)} ancho="max-w-lg"
          pie={<Boton onClick={() => setClave(null)}>Cerrar</Boton>}>
          <p className="text-sm text-gray-600 mb-3">Nueva contraseña del administrador de la cuenta{clave.email ? ` (${clave.email})` : ''}. Compártala por un canal seguro; no se vuelve a mostrar.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg font-mono">{clave.clave}</code>
            <Boton icono={copiado ? Check : Copy} onClick={() => { navigator.clipboard.writeText(clave.clave); setCopiado(true); setTimeout(() => setCopiado(false), 2000); }}>{copiado ? 'Copiada' : 'Copiar'}</Boton>
          </div>
        </Modal>
      )}

      {eliminar && (
        <ConfirmDangerModal
          title={`Eliminar ${t.name}`}
          message="Se borra la base de datos de la cuenta con todas sus empresas, comprobantes y usuarios. No se puede deshacer."
          confirmText={t.slug}
          confirmLabel={`Escriba ${t.slug} para confirmar:`}
          onConfirm={borrar}
          onCancel={() => setEliminar(false)}
        />
      )}
    </SuperadminLayout>
  );
}
